import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { Subscription } from './types'
import { getSubscription } from '../api/subscriptionRepository'
import { authorize } from '@/shared/lib/auth/authorize'
import { PostLogout } from '@/shared/repositories/LogoutRepository'

const SUBSCRIPTION_CACHE_KEY = 'vpn_subscription_cache'
const SUBSCRIPTION_CACHE_TTL = 60 * 1000

interface SubscriptionCacheSnapshot {
  subscription: Subscription | null
  subscriptions: Subscription[]
  hasExtraSubscriptions: boolean
  lastFetchedAt: number
}

function readSubscriptionCache(): SubscriptionCacheSnapshot | null {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const rawCache = window.sessionStorage.getItem(SUBSCRIPTION_CACHE_KEY)

    if (!rawCache) {
      return null
    }

    const parsedCache = JSON.parse(rawCache) as Partial<SubscriptionCacheSnapshot>

    return {
      subscription: parsedCache.subscription ?? null,
      subscriptions: Array.isArray(parsedCache.subscriptions)
        ? parsedCache.subscriptions
        : [],
      hasExtraSubscriptions: Boolean(parsedCache.hasExtraSubscriptions),
      lastFetchedAt:
        typeof parsedCache.lastFetchedAt === 'number'
          ? parsedCache.lastFetchedAt
          : 0,
    }
  } catch (error) {
    console.error('Не удалось прочитать кэш подписки:', error)
    return null
  }
}

function writeSubscriptionCache(snapshot: SubscriptionCacheSnapshot) {
  if (typeof window === 'undefined') {
    return
  }

  try {
    window.sessionStorage.setItem(
      SUBSCRIPTION_CACHE_KEY,
      JSON.stringify(snapshot),
    )
  } catch (error) {
    console.error('Не удалось сохранить кэш подписки:', error)
  }
}

function removeSubscriptionCache() {
  if (typeof window === 'undefined') {
    return
  }

  window.sessionStorage.removeItem(SUBSCRIPTION_CACHE_KEY)
}

export const useSubscriptionStore = defineStore('subscription', () => {
  const cachedSnapshot = readSubscriptionCache()

  const subscription = ref<Subscription | null>(cachedSnapshot?.subscription ?? null)
  const subscriptions = ref<Subscription[]>(cachedSnapshot?.subscriptions ?? [])
  const hasExtraSubscriptions = ref<boolean>(cachedSnapshot?.hasExtraSubscriptions ?? false)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const errorStatus = ref<string | null>(null)
  const lastFetchedAt = ref<number>(cachedSnapshot?.lastFetchedAt ?? 0)
  
  // Добавляем защиту от множественных запросов
  let lastFetchTime = cachedSnapshot?.lastFetchedAt ?? 0
  const FETCH_COOLDOWN = 1000 // 1 секунда между запросами

  function syncSubscriptionCache() {
    writeSubscriptionCache({
      subscription: subscription.value,
      subscriptions: subscriptions.value,
      hasExtraSubscriptions: hasExtraSubscriptions.value,
      lastFetchedAt: lastFetchedAt.value,
    })
  }

  function applySubscriptionState(
    nextSubscription: Subscription | null,
    nextSubscriptions: Subscription[],
    nextHasExtraSubscriptions: boolean,
    fetchedAt: number,
  ) {
    subscription.value = nextSubscription
    subscriptions.value = nextSubscriptions
    hasExtraSubscriptions.value = nextHasExtraSubscriptions
    lastFetchedAt.value = fetchedAt
    syncSubscriptionCache()
  }

  function applySubscriptionResponse(
    responseData: {
      subscription?: Subscription | null
      subscriptions?: Subscription[]
      extra_subscriptions?: boolean
    },
    fetchedAt: number,
  ) {
    const nextSubscriptions = Array.isArray(responseData.subscriptions)
      ? responseData.subscriptions
      : []
    const nextSubscription = responseData.subscription ?? nextSubscriptions[0] ?? null

    applySubscriptionState(
      nextSubscription,
      nextSubscriptions,
      responseData.extra_subscriptions || false,
      fetchedAt,
    )

    if (responseData.subscription) {
      console.log(
        'Set subscription from response.subscription:',
        responseData.subscription,
      )
    }

    if (nextSubscriptions.length > 0) {
      console.log('Set subscriptions array:', nextSubscriptions)
    }
  }

  async function fetchSubscription(all: boolean = false, force: boolean = false) {
    const now = Date.now()
    
    // Если запрос уже выполняется, не запускаем новый
    if (isLoading.value) {
      console.log('Запрос подписки уже выполняется, пропускаем')
      return
    }
    
    // Проверяем, прошло ли достаточно времени с последнего запроса
    if (!force && now - lastFetchTime < FETCH_COOLDOWN) {
      console.log('Слишком частые запросы подписки, пропускаем')
      return
    }

    if (
      !force &&
      lastFetchedAt.value > 0 &&
      now - lastFetchedAt.value < SUBSCRIPTION_CACHE_TTL
    ) {
      console.log('Кэш подписки еще свежий, сетевой запрос не нужен')
      return
    }
    
    lastFetchTime = now
    
    try {
      isLoading.value = true
      error.value = null
      
      const response = await getSubscription(all)
      
      console.log('Subscription API response:', response.data)

      applySubscriptionResponse(response.data, Date.now())
    } catch (err: any) {
      // 204 No Content — подписок нет
      if (err.status === 204) {
        applySubscriptionState(null, [], false, Date.now())
        error.value = null
      } else {
        error.value = err instanceof Error ? err.message : 'Неизвестная ошибка'
      }

      if(err.status === 401) {
        console.log('401==========')
        errorStatus.value = err.status
        await PostLogout()
        await authorize()
      }
    } finally {
      isLoading.value = false
    }
  }

  async function fetchSubscriptionById(id: string) {
    const now = Date.now()
    
    // Если запрос уже выполняется, не запускаем новый
    if (isLoading.value) {
      console.log('Запрос подписки уже выполняется, пропускаем')
      return
    }
    
    // Проверяем, прошло ли достаточно времени с последнего запроса
    if (now - lastFetchTime < FETCH_COOLDOWN) {
      console.log('Слишком частые запросы подписки по ID, пропускаем')
      return
    }
    
    lastFetchTime = now
    
    try {
      isLoading.value = true
      error.value = null
      
      // Сначала загружаем все подписки
      const response = await getSubscription(true)
      
      console.log('Subscription API response:', response.data)
      
      // Ищем подписку по ID в массиве
      if (response.data.subscriptions && response.data.subscriptions.length > 0) {
        subscriptions.value = response.data.subscriptions
        hasExtraSubscriptions.value = response.data.extra_subscriptions || false
        const foundSubscription = response.data.subscriptions.find(sub => sub.id === id)
        
        if (foundSubscription) {
          subscription.value = foundSubscription
          lastFetchedAt.value = Date.now()
          syncSubscriptionCache()
          console.log('Set subscription by ID:', foundSubscription)
        } else {
          error.value = 'Подписка с указанным ID не найдена'
        }
      }
      
      // Если есть одиночная подписка и её ID совпадает
      if (response.data.subscription && response.data.subscription.id === id) {
        subscription.value = response.data.subscription
        hasExtraSubscriptions.value = response.data.extra_subscriptions || false
        lastFetchedAt.value = Date.now()
        syncSubscriptionCache()
        console.log('Set subscription from response.subscription:', response.data.subscription)
      }
    } catch (err: any) {
      // 204 No Content — подписок нет
      if (err.status === 204) {
        applySubscriptionState(null, [], false, Date.now())
        error.value = 'Подписка не найдена'
      } else {
        error.value = err instanceof Error ? err.message : 'Неизвестная ошибка'
      }

      if(err.status === 401) {
        console.log('401==========')
        errorStatus.value = err.status
        await PostLogout()
        await authorize()
      }
    } finally {
      isLoading.value = false
    }
  }

  function clearSubscription() {
    subscription.value = null
    subscriptions.value = []
    hasExtraSubscriptions.value = false
    error.value = null
    lastFetchedAt.value = 0
    removeSubscriptionCache()
  }

  function setCurrentSubscription(sub: Subscription) {
    subscription.value = sub
    lastFetchedAt.value = Date.now()
    syncSubscriptionCache()
  }

  return {
    subscription,
    subscriptions,
    hasExtraSubscriptions,
    isLoading,
    error,
    errorStatus,
    lastFetchedAt,
    fetchSubscription,
    fetchSubscriptionById,
    clearSubscription,
    setCurrentSubscription
  }
}) 
